import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import { Info } from '@phosphor-icons/react';

import { selectPerPayPeriodText } from 'enrollment/services';
import { PsCheckboxTuner, PsRadioTuner, PsSelectTuner } from 'stencilSetup';
import {
  UnumApplicantRateDto,
  UnumCoverageOptionDto,
} from 'enrollment/queries/useUnumGetPlansQuery/types';
import {
  RiderCheckboxContainerStyled,
  RiderItemStyled,
  RiderLinesContainerStyled,
  RiderPPPContainerStyled,
} from 'enrollment/components/RiderBenefits/RiderBenefitsStyled';
import { getPremiumStyled } from 'enrollment/utils';
import { PPPWrapper, RadioGroupStyled } from './UnumStyled';
import { IncrementAmounts } from './UnumIncrements';

export interface UnumApplicantPageProps {
  applicant: UnumApplicantRateDto;
  index: number;
  declineOption: boolean;
  totalCost: { [key: number]: number };
  setTotalCost: (value: { [key: number]: number }) => void;
  inputInvalid: boolean[];
  setErrorValidation: (value: boolean[]) => void;
  dependentName?: string;
}

export const UnumApplicant = ({
  applicant,
  index,
  declineOption,
  totalCost,
  setTotalCost,
  inputInvalid,
  setErrorValidation,
  dependentName,
}: UnumApplicantPageProps) => {
  const { t } = useTranslation('enrollment.plugins');
  const perPayText = useSelector(selectPerPayPeriodText);
  const numPlans = applicant.CoveragePlans.CoveragePlan.length;
  const isMobile = window.innerWidth < 744;

  const [electedPlan, setElectedPlan] = useState<string | null>(null);
  const [electedCoverage, setElectedCoverage] = useState<string | null>(null);
  const [coverageCost, setCoverageCost] = useState<string>('0.00');
  const [ridersChecked, setRidersChecked] = useState<{
    [key: string]: boolean;
  }>({});
  const [applicantCost, setApplicantCost] = useState<number>(0);
  const [showCoverageNumbers, setShowCoverageNumbers] =
    useState<boolean>(false);

  const coveragePlanID = applicant.CoveragePlans.CoveragePlan[0].CoveragePlanID;
  let radioId = applicant.ApplicantType;
  //this is how ps_Spyder does it. Does not check for duplicate applicant types of any other type
  if (applicant.ApplicantType === 'Child') {
    // There could be more than one child enrolling.  We need to make sure the radio button for the different
    // children is unique within the child's enrolling options
    radioId += applicant.DependentID;
  }

  const handleSelectClick = (e: React.MouseEvent<HTMLSelectElement>) => {
    // Toggle the coverage numbers visibility
    e.preventDefault();
    setShowCoverageNumbers(!showCoverageNumbers);
  };

  const handleAmountChange = (
    increment?: UnumCoverageOptionDto,
    id?: number
  ) => {
    if (increment) {
      const selectElement = document.querySelector(
        `select#${applicant.ApplicantType}-${index}-${id}-coverageOptions`
      );
      const electPlanRecord = applicant.CoveragePlans.CoveragePlan.find(
        (plan) => plan.CoveragePlanID === electedPlan
      );

      if (selectElement) {
        selectElement.innerHTML = `<option
          key=${increment.CoverageOptionID}
          value=${increment.CoverageOptionID}
        >
          ${!isMobile ? `${electPlanRecord?.Description} -` : ''} 
            ${
              Number(increment.BenefitCoverage) >= 0
                ? `${getPremiumStyled({
                    premium: increment.BenefitCoverage,
                    valueOnly: true,
                  })} -`
                : increment.Description
            }
            (
            ${getPremiumStyled({ premium: increment.BenefitPremium, valueOnly: true })}
            )
        </option>`;
      }
      setShowCoverageNumbers(false);
      setElectedCoverage(increment.CoverageOptionID);

      let cost = 0;
      if (increment) {
        Object.entries(ridersChecked)
          .filter(([_, value]) => value === true)
          .forEach(([key]) => {
            const checkedOption = increment?.RiderOptions.RiderOption?.find(
              (option) => `${index}-${option.RiderID}` === key
            );
            if (checkedOption) {
              cost = cost + Number(checkedOption.RiderPremium);
            }
          });
        setCoverageCost(increment.BenefitPremium);
        setApplicantCost(Number(increment.BenefitPremium) + cost);
      }
    }
  };

  const handleRadioChange = (planId: string) => {
    setElectedPlan(planId);
    if (planId === 'OptOut') {
      setRidersChecked({});
      setApplicantCost(0);
    } else if (applicant.Riders.Rider && applicant.Riders.Rider.length > 0) {
      const riderMapping: { [key: string]: boolean } = {};
      applicant.Riders.Rider.forEach((rider) => {
        riderMapping[`${index}-${rider.RiderID}`] =
          String(rider.Required) === 'false' ? false : true;
      });
      setRidersChecked(riderMapping);
    }
    const errors = inputInvalid;
    errors[Number(index)] = false;
    setErrorValidation([...errors]);
  };

  const handleRiderCheckboxChange = (
    e: any,
    riderId: string,
    RiderCost: number
  ) => {
    if (e?.target?.tagName === 'INPUT') {
      const updatedRiders = { ...ridersChecked };
      const addOrSubtract: number = !updatedRiders[`${index}-${riderId}`]
        ? 1
        : -1;
      const value = addOrSubtract * RiderCost;
      updatedRiders[`${index}-${riderId}`] =
        !updatedRiders[`${index}-${riderId}`];
      setRidersChecked(updatedRiders);
      setApplicantCost(applicantCost + value);
    }
  };

  useEffect(() => {
    if (
      applicant.Election.CoveragePlanID ||
      (!declineOption && numPlans === 1)
    ) {
      handleRadioChange(applicant.Election.CoveragePlanID || coveragePlanID);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicant.Election.CoveragePlanID, coveragePlanID]);

  useEffect(() => {
    setTotalCost({ ...totalCost, [index]: applicantCost });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicantCost]);

  return (
    <RadioGroupStyled
      key={`${applicant.ApplicantType}-${index}`}
      aria-required="true"
      role="radiogroup"
      aria-labelledby="applicant_title"
      name={`unum[CoveragePlanID${radioId}]`}
    >
      <h3 id="applicant_title" className="mb-0 d-none d-md-flex">
        {applicant.ApplicantType} {t('plan')}{' '}
        {dependentName ? `(${dependentName.trim()})` : ''}
      </h3>
      {applicant.CoveragePlans.CoveragePlan.map((plan, planIndex) => (
        <div key={`${applicant.ApplicantType}-${index}-${planIndex}`}>
          <div className="d-flex justify-content-between">
            <PsRadioTuner
              inputId={`${applicant.ApplicantType}-${index}-${planIndex}`}
              name={`unum[CoveragePlanID${radioId}]`}
              label={plan.Description}
              value={plan.CoveragePlanID}
              checked={
                applicant.Election.CoveragePlanID === plan.CoveragePlanID ||
                (!declineOption && numPlans === 1)
              }
              onPsChange={() => handleRadioChange(plan.CoveragePlanID)}
              onPsBlur={() => handleRadioChange(plan.CoveragePlanID)}
              validationText={t('common:formValidation.fieldCannotBeEmpty')}
              isInvalid={planIndex === 0 ? inputInvalid[index] : false}
            />
            <PPPWrapper>
              {planIndex === 0 && (
                <RiderPPPContainerStyled>
                  {getPremiumStyled({
                    premium: coverageCost,
                    valueOnly: false,
                    perPayText: perPayText,
                  })}
                </RiderPPPContainerStyled>
              )}
            </PPPWrapper>
          </div>
          {planIndex === 0 && inputInvalid[index] && (
            <p
              className="error my-n2 p-0 caption"
              id={`error-${applicant.ApplicantType}-${index}-${planIndex}`}
            >
              <Info size={20} className="mr-2" />
              {t('common:formValidation.fieldCannotBeEmpty')}
            </p>
          )}
          {electedPlan === plan.CoveragePlanID && (
            <>
              {/* required because EOI seperators in dropdown */}
              <ps-select-field
                input-id={`${applicant.ApplicantType}-${index}-${planIndex}-coverageOptions`}
                label={t('choosePlan')}
                name={`unum[CoverageOptionID][${index}]`}
                onMouseDown={handleSelectClick}
              >
                <option value="">{t('unum.choosePlanlist')}</option>
              </ps-select-field>
              <IncrementAmounts
                show={showCoverageNumbers}
                description={plan.Description}
                parentIndex={planIndex}
                onAmountChange={handleAmountChange}
                increments={plan.CoverageOptions.CoverageOption}
              />
            </>
          )}
          {electedPlan === plan.CoveragePlanID &&
            electedCoverage &&
            applicant.Riders.Rider &&
            applicant.Riders.Rider.length > 0 && (
              <div data-testid="rider-benefits-container" className="my-3">
                <p className="semiBold">{t('additionalRiders')}</p>
                <RiderLinesContainerStyled>
                  {applicant.Riders.Rider.map((rider, riderIndex) => {
                    const riderId = rider.RiderID;
                    const linkedRider =
                      plan.CoverageOptions.CoverageOption.find(
                        (coverage) =>
                          coverage.CoverageOptionID === electedCoverage
                      )?.RiderOptions.RiderOption?.find(
                        (riderOption) => riderId === riderOption.RiderID
                      );

                    if (linkedRider) {
                      return (
                        <div key={`${index}-${riderId}`}>
                          <RiderItemStyled
                            className="rider-line"
                            data-testid="rider-item-container"
                          >
                            <RiderCheckboxContainerStyled>
                              <PsCheckboxTuner
                                data-testid={`unum[Riders][${index}][${riderIndex}][Primary]`}
                                inputId={`unum[Riders][${index}][${riderIndex}][Primary]`}
                                name={`unum[Riders][${index}][${riderIndex}][Primary]`}
                                label={rider.RiderCaption}
                                labelClass="rider-checkbox-label-text"
                                checked={
                                  rider.Required === 'false' ? false : true
                                }
                                onClick={(e) =>
                                  handleRiderCheckboxChange(
                                    e,
                                    riderId,
                                    Number(linkedRider?.RiderPremium)
                                  )
                                }
                                value={linkedRider?.RiderOptionID}
                                required={
                                  rider.Required === 'false' ? false : true
                                }
                                requiredWord={t('required')}
                              />
                            </RiderCheckboxContainerStyled>
                            <RiderPPPContainerStyled>
                              {getPremiumStyled({
                                premium: String(linkedRider?.RiderPremium),
                                valueOnly: false,
                                perPayText: perPayText,
                              })}
                            </RiderPPPContainerStyled>
                          </RiderItemStyled>
                          {ridersChecked[`${index}-${riderId}`] &&
                            linkedRider.RiderOptions.RiderOption && (
                              <PsSelectTuner
                                inputId={`rider_select${riderId}-RiderOptions`}
                                label={t('choosePlan')}
                                hideLabel={true}
                                name={`unum[Riders][${index}][${riderIndex}][Secondary]`}
                                required={true}
                              >
                                <option value="">
                                  {t('unum.choosePlanlist')}
                                </option>
                                {linkedRider?.RiderOptions.RiderOption?.map(
                                  (riderOption) => {
                                    return (
                                      <option
                                        key={`${index}-${riderIndex}-${riderOption.RiderID}`}
                                        value={riderOption.RiderOptionID}
                                      >
                                        {typeof riderOption.Caption === 'string'
                                          ? riderOption.Caption
                                          : riderOption.RiderCoverage}{' '}
                                        (
                                        {getPremiumStyled({
                                          premium: riderOption.RiderPremium,
                                          valueOnly: true,
                                        })}
                                        )
                                      </option>
                                    );
                                  }
                                )}
                              </PsSelectTuner>
                            )}
                        </div>
                      );
                    } else {
                      return null;
                    }
                  })}
                </RiderLinesContainerStyled>
              </div>
            )}
        </div>
      ))}
      {declineOption && (
        <PsRadioTuner
          inputId={`${applicant.ApplicantType}-${index}-optOut`}
          name={`unum[CoveragePlanID${radioId}]`}
          label={`${t('decline')} ${t(applicant.ApplicantType)} ${t('coverage')}`}
          value="OptOut"
          onPsChange={() => handleRadioChange('OptOut')}
          onPsBlur={() => handleRadioChange('OptOut')}
        />
      )}
    </RadioGroupStyled>
  );
};
